import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import {
  Box,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CircularProgress from "@mui/material/CircularProgress";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import DialogConfirm from "@jumbo/components/JumboDialog/components/DialogConfirm";
import { deleteLeadImage, uploadLeadImages } from "app/redux/slice/leadSlice";

const MAX_FILE_SIZE = 5 * 1024 * 1024;

// The backend stores bare filenames; REACT_APP_IMAGE_BASE_URL already ends in
// "uploads/", so only the module folder needs to be added here.
const buildImageUrl = (filename) =>
  `${process.env.REACT_APP_IMAGE_BASE_URL}lead-images/${filename}`;

const LeadImages = ({ leadId, images = [], onChanged }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const inputId = `lead-image-upload-${leadId}`;

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Object URLs must be revoked, otherwise the picked blobs stay in memory for
  // as long as the page is open.
  useEffect(() => {
    const urls = selectedFiles.map((file) => URL.createObjectURL(file));
    setPreviews(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [selectedFiles]);

  const handleSelect = (event) => {
    const picked = Array.from(event.target.files || []);
    // Reset the input so picking the same file again still fires onChange.
    event.target.value = "";

    const accepted = [];
    picked.forEach((file) => {
      if (!file.type?.startsWith("image/")) {
        enqueueSnackbar(`${file.name} is not an image file`, {
          variant: "error",
        });
      } else if (file.size > MAX_FILE_SIZE) {
        enqueueSnackbar(`${file.name} is larger than 5MB`, {
          variant: "error",
        });
      } else {
        accepted.push(file);
      }
    });

    if (accepted.length) {
      setSelectedFiles((prev) => [...prev, ...accepted]);
    }
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (!selectedFiles.length) return;
    setUploading(true);

    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append("images", file));

    const response = await dispatch(
      uploadLeadImages({ id: leadId, data: formData })
    ).unwrap();

    const isSuccess = response.status === 200;
    enqueueSnackbar(response.data?.responseMessage, {
      variant: isSuccess ? "success" : "error",
    });
    if (isSuccess) {
      setSelectedFiles([]);
      onChanged?.();
    }
    setUploading(false);
  };

  const handleDelete = async () => {
    const filename = deleteTarget;
    setDeleteTarget(null);

    const response = await dispatch(
      deleteLeadImage({ id: leadId, filename })
    ).unwrap();

    const isSuccess = response.status === 200;
    enqueueSnackbar(response.data?.responseMessage, {
      variant: isSuccess ? "success" : "error",
    });
    if (isSuccess) onChanged?.();
  };

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" mb={0}>
          Images
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {images.length ? `${images.length} uploaded` : "None uploaded yet"}
        </Typography>
      </Stack>

      <input
        id={inputId}
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={handleSelect}
      />
      <label htmlFor={inputId}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            p: 2,
            mb: 2,
            border: "2px dashed #ccc",
            borderRadius: 2,
            minHeight: 120,
            cursor: "pointer",
            "&:hover": { borderColor: "primary.main" },
          }}
        >
          <CloudUploadIcon color="primary" fontSize="large" />
          <Typography variant="body1" mb={0}>
            Click to upload images
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={0}>
            JPG, PNG or WEBP — up to 5MB each, multiple allowed
          </Typography>
        </Box>
      </label>

      {selectedFiles.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {selectedFiles.length} image(s) ready to upload
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {selectedFiles.map((file, index) => (
              <Grid item key={`${file.name}-${index}`}>
                <Paper
                  elevation={2}
                  sx={{
                    position: "relative",
                    width: 100,
                    height: 100,
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => removeSelectedFile(index)}
                    sx={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      bgcolor: "rgba(255,255,255,0.85)",
                      "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  {previews[index] && (
                    <img
                      src={previews[index]}
                      alt={file.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
          <Stack direction="row" spacing={2}>
            <LoadingButton
              variant="contained"
              onClick={handleUpload}
              loading={uploading}
              loadingIndicator={<CircularProgress size={20} />}
            >
              Upload
            </LoadingButton>
            <LoadingButton
              variant="outlined"
              color="secondary"
              onClick={() => setSelectedFiles([])}
              disabled={uploading}
            >
              Clear
            </LoadingButton>
          </Stack>
        </Box>
      )}

      {images.length > 0 && (
        <Grid container spacing={2}>
          {images.map((filename) => (
            <Grid item xs={12} sm={4} md={3} key={filename}>
              <Paper
                elevation={3}
                sx={{
                  position: "relative",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <Tooltip title="Delete image">
                  <IconButton
                    size="small"
                    onClick={() => setDeleteTarget(filename)}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      bgcolor: "rgba(255,255,255,0.85)",
                      "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                    }}
                  >
                    <DeleteIcon fontSize="small" color="error" />
                  </IconButton>
                </Tooltip>
                <a
                  href={buildImageUrl(filename)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={buildImageUrl(filename)}
                    alt={filename}
                    style={{
                      width: "100%",
                      height: 160,
                      objectFit: "cover",
                      display: "block",
                      cursor: "pointer",
                    }}
                  />
                </a>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <DialogConfirm
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Are you sure you want to delete this image?"
        onConfirm={handleDelete}
        btnLabels={{ cancel: "Cancel", confirm: "Delete" }}
      />
    </Box>
  );
};

export default LeadImages;
